import { PeriodApys, UiVaultConfig, VaultStats } from 'src/@types/vaults';
import { VaultManagerIcon } from './VaultManagerIcon';
import { Open, Details, Swap } from '@drift-labs/icons';
import { cj } from 'src/utils/classNames';
import { useDrawersStore, useDrawerToggler } from 'src/stores/useDrawersStore';
import { getApyToUse, getAuthorityVaultBalance } from 'src/utils/vault/math';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import {
	VAULT_APY_TOOLTIP_CONTENT,
	VAULT_TARGET_APY_TOOLTIP_CONTENT,
	VAULT_TEMP_APY_LABEL,
} from 'src/constants/vaults/misc';
import SkeletonValuePlaceholder from '../SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import MarketIcon from '../Utils/MarketIcon';
import { MarketId, UIMarket } from '@drift/common';
import { VaultTags } from './VaultTags';
import { VaultDepositor } from '@drift-labs/vaults-sdk';
import { BigNum, QUOTE_PRECISION_EXP } from '@drift-labs/sdk';
import { VaultStatsWithDepositor } from '../../hooks/vaults/useVaultStatsWithDepositors';
import useGetOraclePriceForMarket from '../../hooks/useGetOraclePriceForMarket';
import { VaultTvlProgressDisplay } from './VaultTvlProgressDisplay';
import { useSpotMarketsLookup } from 'src/hooks/spotMarkets/useSpotMarketsLookup';
import { getApyColumnHeader } from './utils';
import { useRoiForVault } from './hooks';
import { PageRoute, VAULT_VIEW_TAB_OPTIONS } from 'src/constants/constants';
import Button from '../primitives/Button/Button';
import { BalanceDisplayType } from 'src/@types/types';
import IconButton from '../primitives/IconButton/IconButton';

export const VaultTableVaultComparisonCard = (props: {
	vaultStat: VaultStats;
	uiVaultConfig: UiVaultConfig;
	vaultDepositorAccounts: VaultDepositor[];
	apyPeriod: string;
	balanceDisplayType: BalanceDisplayType;
	onToggleBalanceDisplayType: () => void;
}) => {
	const apyForPeriod =
		props.vaultStat.apys[props.apyPeriod as keyof PeriodApys];
	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const toggleVaultDetails = useDrawerToggler('vaultDetails');
	const set = useDrawersStore((state) => state.set);

	if (!apyForPeriod || isNaN(apyForPeriod)) {
		return <SkeletonValuePlaceholder className="h-[235px] w-full" loading />;
	}

	const { isUsingTemporaryApy, apy, apyString } = getApyToUse(
		props.uiVaultConfig,
		apyForPeriod,
		props.vaultStat.numOfVaultSnapshots
	);

	const authorityVaultBalance = getAuthorityVaultBalance(
		props.vaultDepositorAccounts,
		{ ...props.vaultStat, uiVaultConfig: props.uiVaultConfig },
		'base'
	);

	const oraclePrice =
		getOraclePriceForMarket(
			MarketId.createSpotMarket(props.uiVaultConfig.depositAsset)
		) ?? BigNum.zero(QUOTE_PRECISION_EXP);

	const authorityVaultBalanceNotional = authorityVaultBalance.mul(oraclePrice);

	const openVaultDetailsDrawer = (e: React.MouseEvent) => {
		e.stopPropagation();
		set((store) => {
			store.vaultDetails.vault = props.uiVaultConfig;
		});
		toggleVaultDetails(true);
	};

	const apyHeader = getApyColumnHeader(props.apyPeriod);

	return (
		<div className="flex flex-col gap-y-lg rounded-sm bg-container-bg p-md">
			<div className="flex items-center gap-x-md">
				<VaultManagerIcon
					uiVaultConfig={props.uiVaultConfig}
					size={40}
					assetSize={14}
				/>
				<div className="flex w-full flex-col gap-y-xs overflow-hidden">
					<div className="flex w-full items-center justify-between gap-1 overflow-hidden">
						<div className="flex items-center gap-1 overflow-hidden">
							<span className="truncate typo-t2">
								{props.uiVaultConfig.name}
							</span>
						</div>
					</div>
					<VaultTags
						uiVaultConfig={props.uiVaultConfig}
						numOfVaultSnapshots={props.vaultStat.numOfVaultSnapshots}
					/>
				</div>
			</div>

			<div className="flex flex-col gap-1">
				<span className="typo-b4 text-text-secondary">TVL</span>
				<VaultTvlProgressDisplay
					vaultStats={props.vaultStat}
					uiVaultConfig={props.uiVaultConfig}
				/>
			</div>

			<div className="flex w-full items-center justify-between">
				<div className="flex flex-col gap-y-xs">
					<Tooltip
						className="typo-b4 text-text-secondary"
						content={
							isUsingTemporaryApy
								? VAULT_TARGET_APY_TOOLTIP_CONTENT
								: VAULT_APY_TOOLTIP_CONTENT
						}
					>
						{isUsingTemporaryApy ? VAULT_TEMP_APY_LABEL : apyHeader}
					</Tooltip>
					<span
						className={cj(
							'typo-t3',
							isUsingTemporaryApy
								? 'text-purple-link'
								: apy > 0
									? 'text-text-positive-green-button'
									: 'text-negative-red'
						)}
					>
						{apyString}
					</span>
				</div>

				<div className="flex flex-col items-end gap-y-xs">
					<span className="typo-b4 text-text-secondary">Age</span>
					<div className="typo-t3">{`${props.vaultStat.numOfVaultSnapshots} days`}</div>
				</div>
			</div>

			{authorityVaultBalance.gtZero() && (
				<div className="flex w-full justify-between">
					<div className="flex flex-col gap-y-xs">
						<div className="flex items-center gap-x-xs">
							<span className="typo-b4 text-text-secondary">Your Balance</span>
							<IconButton
								size="sm"
								variant="tertiary"
								onClick={(e) => {
									e.stopPropagation();
									props.onToggleBalanceDisplayType();
								}}
								icon={Swap}
							/>
						</div>
						<div className="flex items-end justify-start gap-x-xs typo-t3">
							{props.balanceDisplayType === 'base' ? (
								<>
									<MarketIcon
										marketSymbol={
											UIMarket.createSpotMarket(
												props.uiVaultConfig.depositAsset
											).market.symbol
										}
									/>
									<span
										className={cj(
											authorityVaultBalance.gtZero()
												? 'text-text-default'
												: 'text-text-disabled'
										)}
									>
										{authorityVaultBalance.toMillified(4)}
									</span>
								</>
							) : (
								<span
									className={cj(
										authorityVaultBalanceNotional.gtZero()
											? 'text-text-default'
											: 'text-text-disabled'
									)}
								>
									{authorityVaultBalanceNotional.toNotional()}
								</span>
							)}
						</div>
					</div>
				</div>
			)}

			<div className="flex w-full items-center gap-2">
				<Button
					size="sm"
					variant="secondary"
					className="flex-1"
					as="a"
					href={
						props.uiVaultConfig.externalUiLink
							? props.uiVaultConfig.externalUiLink
							: `/${PageRoute.vaults}/strategy-vaults/${props.uiVaultConfig.vaultPubkeyString}`
					}
					openInNewTab={!!props.uiVaultConfig.externalUiLink}
					rightIcon={props.uiVaultConfig.externalUiLink ? Open : undefined}
				>
					View Vault
				</Button>
				<Button
					size="sm"
					variant="secondary"
					className="flex-1"
					onClick={openVaultDetailsDrawer}
					rightIcon={Details}
				>
					Details
				</Button>
			</div>
		</div>
	);
};

export const VaultTableCurrentPositionCard = ({
	vaultStatAndDepositor,
	uiVaultConfig,
	apyPeriod,
	isClosedPosition,
	balanceDisplayType,
	onToggleBalanceDisplayType,
	isPortfolioPage = false,
}: {
	vaultStatAndDepositor: VaultStatsWithDepositor;
	uiVaultConfig: UiVaultConfig;
	apyPeriod: string;
	isClosedPosition?: boolean;
	balanceDisplayType: BalanceDisplayType;
	onToggleBalanceDisplayType: () => void;
	isPortfolioPage?: boolean;
}) => {
	const spotMarketsLookup = useSpotMarketsLookup();
	const toggleVaultDetails = useDrawerToggler('vaultDetails');
	const set = useDrawersStore((state) => state.set);
	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const roi = useRoiForVault(vaultStatAndDepositor);
	const apyForPeriod =
		vaultStatAndDepositor.vaultStat.apys[apyPeriod as keyof PeriodApys];

	if (!apyForPeriod || isNaN(apyForPeriod)) {
		return <SkeletonValuePlaceholder className="h-[235px] w-full" loading />;
	}

	const { isUsingTemporaryApy, apy, apyString } = getApyToUse(
		uiVaultConfig,
		apyForPeriod,
		vaultStatAndDepositor.vaultStat.numOfVaultSnapshots
	);

	const authorityVaultBalance = getAuthorityVaultBalance(
		vaultStatAndDepositor.depositor
			? [vaultStatAndDepositor.depositor.vaultDepositor]
			: [],
		{
			...vaultStatAndDepositor.vaultStat,
			uiVaultConfig: uiVaultConfig,
		},
		'base'
	);

	const earnings = authorityVaultBalance.sub(
		BigNum.from(
			vaultStatAndDepositor.depositor?.vaultDepositor.netDeposits,
			spotMarketsLookup[
				vaultStatAndDepositor.vaultStat.uiVaultConfig.depositAsset
			].precisionExp
		)
	);

	const oraclePrice =
		getOraclePriceForMarket(
			MarketId.createSpotMarket(
				vaultStatAndDepositor.vaultStat.uiVaultConfig.depositAsset
			)
		) ?? BigNum.zero(QUOTE_PRECISION_EXP);

	const earningsNotional = earnings.mul(oraclePrice);
	const authorityVaultBalanceNotional = authorityVaultBalance.mul(oraclePrice);

	const handleViewVault = (e: React.MouseEvent) => {
		e.stopPropagation();
		set((store) => {
			store.vaultDetails.vault = uiVaultConfig;
		});
		toggleVaultDetails(true);
	};

	const apyHeader = getApyColumnHeader(apyPeriod);

	return (
		<div className="flex flex-col gap-y-lg rounded-sm bg-container-bg p-md">
			<div className="flex items-center gap-x-md">
				<VaultManagerIcon uiVaultConfig={uiVaultConfig} size={40} />
				<div className="flex w-full flex-col gap-y-xs overflow-hidden">
					<span className="truncate typo-t2 text-text-default">
						{uiVaultConfig.name}
					</span>
					<VaultTags
						uiVaultConfig={uiVaultConfig}
						numOfVaultSnapshots={
							vaultStatAndDepositor.vaultStat.numOfVaultSnapshots
						}
					/>
				</div>
			</div>
			{!isClosedPosition && !isPortfolioPage && (
				<div className="flex w-full items-center justify-between">
					<div className="flex flex-col gap-y-xs">
						<Tooltip
							className="typo-b4 text-text-secondary"
							content={
								isUsingTemporaryApy
									? VAULT_TARGET_APY_TOOLTIP_CONTENT
									: VAULT_APY_TOOLTIP_CONTENT
							}
						>
							{isUsingTemporaryApy ? VAULT_TEMP_APY_LABEL : apyHeader}
						</Tooltip>
						<span
							className={cj(
								'typo-t3',
								isUsingTemporaryApy
									? 'text-purple-link'
									: apy > 0
										? 'text-text-positive-green-button'
										: 'text-text-negative-red-button'
							)}
						>
							{apyString}
						</span>
					</div>

					<div className="flex flex-col items-end gap-y-xs">
						<span className="typo-b4 text-text-secondary">Your ROI</span>
						<div className="typo-t3 text-text-default">
							{roi ? `${roi.toFixed(2)}%` : '-'}
						</div>
					</div>
				</div>
			)}

			<div className="flex w-full justify-between">
				<div className="flex flex-col gap-y-xs">
					<div className="flex items-center gap-x-xs">
						<span className="typo-b4 text-text-secondary">Your Balance</span>
						<IconButton
							size="sm"
							variant="tertiary"
							onClick={(e) => {
								e.stopPropagation();
								onToggleBalanceDisplayType();
							}}
							icon={Swap}
						/>
					</div>
					<div className="flex items-end justify-start gap-x-xs typo-t3">
						{balanceDisplayType === 'base' ? (
							<>
								<MarketIcon
									marketSymbol={
										UIMarket.createSpotMarket(uiVaultConfig.depositAsset).market
											.symbol
									}
								/>
								<span
									className={cj(
										authorityVaultBalance.gtZero()
											? 'text-text-default'
											: 'text-text-disabled'
									)}
								>
									{authorityVaultBalance.toMillified(4)}
								</span>
							</>
						) : (
							<span
								className={cj(
									authorityVaultBalanceNotional.gtZero()
										? 'text-text-default'
										: 'text-text-disabled'
								)}
							>
								{authorityVaultBalanceNotional.toNotional()}
							</span>
						)}
					</div>
				</div>
				<div className="flex flex-col items-end gap-y-xs">
					<div className="flex items-center gap-x-xs">
						<span className="typo-b4 text-text-secondary">
							Your Earnings (All-Time)
						</span>
						<IconButton
							size="sm"
							variant="tertiary"
							onClick={(e) => {
								e.stopPropagation();
								onToggleBalanceDisplayType();
							}}
							icon={Swap}
						/>
					</div>
					<div className="flex items-end gap-x-xs typo-t3">
						{balanceDisplayType === 'base' ? (
							<>
								<MarketIcon
									marketSymbol={
										UIMarket.createSpotMarket(uiVaultConfig.depositAsset).market
											.symbol
									}
								/>
								<span className="text-text-default">
									{earnings.toMillified(4)}
								</span>
							</>
						) : (
							<span className="text-text-default">
								{earningsNotional.toNotional()}
							</span>
						)}
					</div>
				</div>
			</div>
			<div className="flex w-full items-center gap-sm">
				<Button
					size="sm"
					variant="secondary"
					className="flex-1"
					as="a"
					href={
						uiVaultConfig.externalUiLink
							? uiVaultConfig.externalUiLink
							: `/${PageRoute.vaults}/strategy-vaults/${uiVaultConfig.vaultPubkeyString}?tab=${VAULT_VIEW_TAB_OPTIONS.UserPerformance}`
					}
					openInNewTab={!!uiVaultConfig.externalUiLink}
					rightIcon={uiVaultConfig.externalUiLink ? Open : undefined}
				>
					Manage
				</Button>
				{!isPortfolioPage && (
					<Button
						size="sm"
						variant="secondary"
						className="flex-1"
						onClick={handleViewVault}
						rightIcon={Details}
					>
						Details
					</Button>
				)}
			</div>
		</div>
	);
};
